import * as React from 'react';
// @ts-expect-error
import {FixedSizeList} from 'react-window';

import * as ramda from 'ramda';

import {findSpecialState} from './table-component';
import type {Row, SpecialState, VirtualizeProps, HeaderGroup} from './types';

const DEFAULT_OVERSCAN_COUNT = 50;

type TableRowProps<T> = {
    rows: Row<T>[];
    renderRow: (row: Row<T>) => React.ReactElement;
    renderFixedSizeListRows: (arg1: {index: number; style: any}) => React.ReactElement;
    virtualizeProps?: VirtualizeProps;
    specialStates?: SpecialState[];
    expandProps?: {
        renderExpandCell: (row: Row<T>) => React.ReactElement;
        renderSubComponent: (row: Row<T>) => React.ReactElement;
        expandRowIndex?: number | null | undefined;
        clearExpandRowIndex?: () => void;
        hideExpandCell?: boolean;
        matchTableWidth?: boolean;
        expandAllRows?: boolean;
    };
};

type CompareTableRowProps<T> = TableRowProps<T> & {
    headerGroups: HeaderGroup[];
};

function MemoizedTableRowsComponent<T>(props: TableRowProps<T>): React.ReactElement {
    const specialState = props.specialStates ? findSpecialState(props.specialStates) : undefined;

    return props.virtualizeProps ? (
        <FixedSizeList
            className='fixed-size-list'
            height={specialState ? 0 : props.virtualizeProps.height}
            itemCount={props.rows.length}
            itemSize={props.virtualizeProps.itemSize}
            width={props.virtualizeProps.width || '100%'}
            overscanCount={props.virtualizeProps.overscanCount || DEFAULT_OVERSCAN_COUNT}
            style={props.virtualizeProps.style}
        >
            {props.renderFixedSizeListRows}
        </FixedSizeList>
    ) : (
        <>{props.rows.map((row: Row<T>) => props.renderRow(row))}</>
    );
}

// This controls when to re-render the table rows based on the CompareTableRowProps
export const MemoizedTableRows: <T>(props: CompareTableRowProps<T>) => React.ReactElement =
    React.memo(MemoizedTableRowsComponent, (prevProps, nextProps) => {
        // Our useExpanded plugin handles state management internally which
        // means we won't see updates immediately here. We'll just disable
        // memoization for tables that use expandProps.
        if (prevProps.expandProps || nextProps.expandProps) {
            return false;
        }

        // @ts-expect-error
        if (!ramda.equals(prevProps.headerGroups, nextProps.headerGroups)) {
            return false;
        }

        if (!ramda.equals(prevProps.rows, nextProps.rows)) {
            return false;
        }

        if (prevProps.renderRow !== nextProps.renderRow) {
            return false;
        }

        if (!ramda.equals(prevProps.virtualizeProps, nextProps.virtualizeProps)) {
            return false;
        }

        if (!ramda.equals(prevProps.specialStates, nextProps.specialStates)) {
            return false;
        }

        if (prevProps.renderFixedSizeListRows !== nextProps.renderFixedSizeListRows) {
            return false;
        }

        return true;
    }) as any;
